import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { getApi, postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import ScreeningFormModal from './ScreeningFormModal';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// Load Stripe with error handling - prevents app crash when Stripe.js fails to load (network, ad blocker, etc.)
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey).catch((err) => {
      console.warn('Stripe failed to load. Payment may be unavailable.', err?.message || err);
      return null;
    })
  : null;

const elementOptions = {
  style: {
    base: {
      fontSize: '14px',
      color: '#2c2c2c',
      fontFamily:
        "'Be Vietnam Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      '::placeholder': {
        color: '#9aa0a6',
        fontFamily: "'Be Vietnam Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      },
    },
    invalid: { color: '#dc3545' },
  },
};

function formatMoney(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function ScreeningPaymentForm({
  amountCents = 999,
  originalAmountCents,
  discountCents = 0,
  clientSecret,
  landlordName,
  onPaymentSuccess,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);

  const feeCents = originalAmountCents ?? amountCents;
  const amountText = useMemo(() => formatMoney(amountCents), [amountCents]);
  const feeText = useMemo(() => formatMoney(feeCents), [feeCents]);
  const discountText = useMemo(() => formatMoney(discountCents), [discountCents]);
  const hasDiscount = discountCents > 0;

  // Browser autofill fills the whole card in one shot, so the field jumps from
  // empty straight to complete in a single change event. Moving focus in the
  // middle of that cancels the fill and leaves the card input stuck - it looks
  // disabled and stops accepting typing. Typing produces a change per
  // keystroke, so only auto-advance once we have seen more than one change,
  // and never onto a field autofill has already filled.
  const numberEdits = useRef(0);
  const expiryEdits = useRef(0);

  const handleCardNumberChange = (event) => {
    numberEdits.current = event.empty ? 0 : numberEdits.current + 1;
    if (!event.complete || numberEdits.current < 2 || !elements) return;
    const expiryElement = elements.getElement(CardExpiryElement);
    if (expiryElement) setTimeout(() => expiryElement.focus(), 50);
  };

  const handleExpiryChange = (event) => {
    expiryEdits.current = event.empty ? 0 : expiryEdits.current + 1;
    if (!event.complete || expiryEdits.current < 2 || !elements) return;
    const cvcElement = elements.getElement(CardCvcElement);
    if (cvcElement) setTimeout(() => cvcElement.focus(), 50);
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!clientSecret) {
      toast.error('Payment is not ready yet. Please wait a moment and try again.');
      return;
    }
    if (!stripe || !elements) return;

    setIsLoading(true);
    try {
      const cardNumber = elements.getElement(CardNumberElement);
      if (!cardNumber) {
        toast.error('Card element not ready. Please try again.');
        return;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardNumber },
      });

      if (error) {
        toast.error(error.message || 'Payment failed. Please try again.');
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        toast.success('Payment successful');
        onPaymentSuccess?.();
      } else {
        toast.info(`Payment status: ${paymentIntent?.status || 'unknown'}`);
      }
    } catch (err) {
      toast.error(err?.message || 'Payment failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tenant-screening-payment-form">
      <div className="tenant-screening-payment-header">
        <div className="tenant-screening-payment-icon-wrap">
          <svg className="tenant-screening-payment-wallet-icon" width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="72" height="72" rx="14" fill="#43BF67" fillOpacity="0.34"/>
            <path d="M22 26h28a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H22a2 2 0 0 1-2-2V28a2 2 0 0 1 2-2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M26 26V22a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="38" y="36" width="8" height="4" rx="1" fill="white"/>
          </svg>
        </div>
        <h1 className="tenant-screening-payment-title">Complete Payment</h1>
        <p className="tenant-screening-payment-subtitle">Secure payment for tenant screening</p>
      </div>

      <div className="payment-summary-card tenant-screening-summary-card">
        <div className="payment-summary-row">
          <div className="payment-summary-label">Tenant Screening Fee</div>
          <div className="payment-summary-price">{feeText}</div>
        </div>
        {hasDiscount && (
          <div className="payment-summary-row payment-summary-discount">
            <div className="payment-summary-label">Discount</div>
            <div className="payment-summary-price payment-summary-discount-value">-{discountText}</div>
          </div>
        )}
        <div className="payment-summary-divider" />
        <div className="payment-summary-row total">
          <div className="payment-summary-label">Total</div>
          <div className="payment-summary-total">{amountText}</div>
        </div>
        <div className="payment-summary-footnote">
          Valid for all properties of {landlordName || 'this landlord'}.
        </div>
      </div>

      <form onSubmit={handlePay} className="payment-card-form tenant-screening-card-form" noValidate autoComplete="off" data-form-type="other">
        <div className="payment-field">
          <label className="payment-label">Card Number</label>
          <div className="payment-stripe-input payment-stripe-input-with-icon" data-autocomplete="off">
            <svg className="payment-card-icon" width="20" height="16" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0.5" y="3.5" width="23" height="12" rx="1.5" stroke="#9aa0a6" strokeWidth="1"/>
              <rect x="2" y="6" width="20" height="2" rx="1" fill="#9aa0a6"/>
              <rect x="2" y="10" width="8" height="2" rx="1" fill="#9aa0a6"/>
            </svg>
            <CardNumberElement
              options={{ ...elementOptions, disableLink: true }}
              onChange={handleCardNumberChange}
            />
          </div>
        </div>

        <div className="payment-row">
          <div className="payment-field">
            <label className="payment-label">Expiry</label>
            <div className="payment-stripe-input" data-autocomplete="off">
              <CardExpiryElement
                options={{ ...elementOptions, disableLink: true }}
                onChange={handleExpiryChange}
              />
            </div>
          </div>
          <div className="payment-field">
            <label className="payment-label">CVC</label>
            <div className="payment-stripe-input" data-autocomplete="off">
              <CardCvcElement options={{ ...elementOptions, disableLink: true }} />
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="payment-submit-button tenant-screening-pay-button"
          disabled={isLoading || !clientSecret}
        >
          {isLoading ? 'Processing...' : `Pay ${amountText}`}
        </button>
      </form>
    </div>
  );
}

const TenantScreeningPayment = () => {
  const { id: propertyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const landlordName = location.state?.landlordName || 'this landlord';
  const propertyAddress = location.state?.propertyAddress || 'this property';
  const propertyCode = location.state?.propertyCode || '';
  const initialClientSecret = location.state?.clientSecret || '';
  const initialAmount = location.state?.amount;
  const initialOriginalAmount = location.state?.originalAmount;
  const initialDiscountAmount = location.state?.discountAmount;
  const paymentAlreadyDoneFromNav = location.state?.paymentAlreadyDone === true;

  const [isInitLoading, setIsInitLoading] = useState(true);
  const [skipPaymentFlow, setSkipPaymentFlow] = useState(paymentAlreadyDoneFromNav);
  const [initError, setInitError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(
    initialAmount ? { id: 'screening', name: 'Tenant Screening Fee', price: initialAmount } : null
  );
  const [clientSecret, setClientSecret] = useState(initialClientSecret);
  const [showSuccess, setShowSuccess] = useState(false);
  const [screeningLinkData, setScreeningLinkData] = useState(null);
  const [isCopying, setIsCopying] = useState(false);
  const [isScreeningFormOpen, setIsScreeningFormOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initPayment = async () => {
      setIsInitLoading(true);
      setInitError('');
      try {
        let shouldSkipPayment = paymentAlreadyDoneFromNav;

        if (!shouldSkipPayment) {
          try {
            const propRes = await getApi(`/property/${propertyId}`);
            const tenantExtra = propRes?.data?.tenant_extra;
            if (tenantExtra && String(tenantExtra.is_payment) === '1') {
              shouldSkipPayment = true;
              if (!cancelled) setSkipPaymentFlow(true);
            }
          } catch (e) {
            console.error('Error checking payment status:', e);
          }
        }

        // Payment already done: skip payment form, fetch link and show Begin Screening
        if (shouldSkipPayment) {
          try {
            const res = await postApi('/screening/generate-link', {
              property_id: propertyId,
            });
            if (cancelled) return;
            const root = res?.data || res;
            const data = root?.data || root;
            setScreeningLinkData({
              screeningId: data?.screening_id,
              token: data?.token,
              link: data?.link || '',
              expiresIn: data?.expires_in,
            });
            setShowSuccess(true);
            if (!cancelled) setIsInitLoading(false);
          } catch (e) {
            if (!cancelled) {
              const msg = e?.response?.data?.message || e?.message || 'Failed to generate screening link';
              setInitError(msg);
              toast.error(msg);
            }
          }
          if (!cancelled) setIsInitLoading(false);
          return;
        }

        // If we already have clientSecret from previous step (proceed button), just finish init
        if (initialClientSecret) {
          if (!selectedPlan) {
            const price = Number(initialAmount ?? 9.99);
            if (!cancelled) {
              setSelectedPlan({ id: 'screening', name: 'Tenant Screening Fee', price });
            }
          }
          if (!cancelled) {
            setClientSecret(initialClientSecret);
          }
          return;
        }

        // Otherwise, create a new payment intent for this property
        const createRes = await postApi('/subscriptions/create-intent', {
          property_id: propertyId,
        });

        const data = createRes?.data || createRes;
        const cs = data?.client_secret || data?.clientSecret || '';
        const amount = data?.amount ?? 9.99;

        if (!cs) {
          throw new Error(createRes?.message || 'Missing client_secret from tenant screening create-intent');
        }

        if (!cancelled) {
          setClientSecret(cs);
          setSelectedPlan({ id: 'screening', name: 'Tenant Screening Fee', price: amount });
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.message || e?.message || 'Failed to load screening payment options';
          setInitError(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setIsInitLoading(false);
      }
    };

    initPayment();
    return () => { cancelled = true; };
  }, [propertyId, initialClientSecret, initialAmount, selectedPlan, paymentAlreadyDoneFromNav]);

  const amountCents = useMemo(() => {
    const price = Number(selectedPlan?.price);
    if (!Number.isFinite(price)) return 999;
    return Math.round(price * 100);
  }, [selectedPlan?.price]);

  const originalAmountCents = useMemo(() => {
    const original = Number(initialOriginalAmount);
    if (Number.isFinite(original) && original > 0) return Math.round(original * 100);
    return amountCents;
  }, [initialOriginalAmount, amountCents]);

  const discountCents = useMemo(() => {
    const discount = Number(initialDiscountAmount);
    if (Number.isFinite(discount) && discount > 0) return Math.round(discount * 100);
    return Math.max(0, originalAmountCents - amountCents);
  }, [initialDiscountAmount, originalAmountCents, amountCents]);

  const handlePaymentSuccess = async () => {
    try {
      const res = await postApi('/screening/generate-link', {
        property_id: propertyId,
      });

      const root = res?.data || res;
      const data = root?.data || root;

      setScreeningLinkData({
        screeningId: data?.screening_id,
        token: data?.token,
        link: data?.link || '',
        expiresIn: data?.expires_in,
      });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to generate screening link';
      toast.error(msg);
      setScreeningLinkData(null);
    } finally {
      setShowSuccess(true);
    }
  };

  const handleCopyLink = async () => {
    if (!screeningLinkData?.link || isCopying) return;
    const text = screeningLinkData.link;
    setIsCopying(true);
    try {
      if (navigator?.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
        } finally {
          document.body.removeChild(textarea);
        }
      }
      toast.success('Screening link copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy link. Please copy it manually.');
    } finally {
      setIsCopying(false);
    }
  };

  const handleOpenScreeningLink = () => {
    const link = screeningLinkData?.link;
    if (!link) return;
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  // When payment already done: show only loading until we have the link (never flash the payment form)
  if (skipPaymentFlow && isInitLoading && !showSuccess && !initError) {
    return (
      <div className="tenant-screening-payment-page">
        <div className="tenant-screening-payment-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <div className="tenant-screening-payment-loading">Preparing your screening link...</div>
        </div>
      </div>
    );
  }
  if (skipPaymentFlow && initError) {
    return (
      <div className="tenant-screening-payment-page">
        <div className="tenant-screening-payment-content" style={{ padding: '2rem', textAlign: 'center' }}>
          <div className="tenant-screening-payment-error">{initError}</div>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="tenant-screening-payment-page tenant-screening-success-page">
        {/* Screenshot 1: Payment confirmation on gray background (not inside the card) */}
        <div className="tenant-screening-success-top">
          <div className="tenant-screening-success-icon-wrap">
            <img
              src="/assets/images/paySuccess.svg"
              alt=""
              className="tenant-screening-success-icon"
            />
          </div>
          <h1 className="tenant-screening-success-title">Payment successful</h1>
          <p className="tenant-screening-success-message">
            Thankyou for your payment.
          </p>
        </div>

        {/* Screenshot 2: White card with Begin Application Screening content */}
        <div className="tenant-screening-success-card">
          <div className="tenant-screening-success-details">
            <h2 className="tenant-screening-success-subtitle">Begin Application Screening</h2>
            <p className="tenant-screening-success-text">
              To complete your application, please proceed with the automated voice screening process.
            </p>
            <p className="tenant-screening-success-text">
              Select the link below to open it on your mobile device and begin the screening.
            </p>
            <div className="tenant-screening-success-meta">
              <div>
                <strong>Property Address:</strong> {propertyAddress}
              </div>
              {propertyCode && (
                <div>
                  <strong>Property Code:</strong> {propertyCode}
                </div>
              )}
            </div>

            <div className="screening-link-input-wrapper">
              <button
                type="button"
                className="screening-link-input"
                onClick={handleOpenScreeningLink}
                disabled={!screeningLinkData?.link}
              >
                Click to begin screening
              </button>
              <button
                type="button"
                className="screening-link-copy-btn"
                onClick={handleCopyLink}
                disabled={!screeningLinkData?.link || isCopying}
                title="Copy link"
              >
                <img src="/assets/images/CopyIcon.svg" alt="Copy" className="screening-link-copy-icon" />
              </button>
            </div>

            <div className="screening-link-or-divider">
              <span>OR</span>
            </div>

            <div className="screening-link-input-wrapper">
              <button
                type="button"
                className="screening-link-input"
                onClick={() => setIsScreeningFormOpen(true)}
              >
                Click to fill screening form
              </button>
            </div>

            <p className="tenant-screening-success-note">
              <strong>Important:</strong> Please ensure that you answer all questions clearly and accurately. Your
              responses will be submitted to the landlord for review as part of your application.
            </p>
          </div>
        </div>

        <ScreeningFormModal
          isOpen={isScreeningFormOpen}
          onClose={() => setIsScreeningFormOpen(false)}
          propertyId={propertyId}
          screeningId={screeningLinkData?.screeningId}
          onSubmitted={() => {
            setIsScreeningFormOpen(false);
            navigate(`/tenant/properties/${propertyId}`, { replace: true });
          }}
        />
      </div>
    );
  }

  return (
    <div className="tenant-screening-payment-page">
      <div className="tenant-screening-payment-content">
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <ScreeningPaymentForm
              amountCents={amountCents}
              originalAmountCents={originalAmountCents}
              discountCents={discountCents}
              clientSecret={clientSecret}
              landlordName={landlordName}
              onPaymentSuccess={handlePaymentSuccess}
            />
            {isInitLoading && (
              <div className="tenant-screening-payment-loading">
                Loading...
              </div>
            )}
            {!isInitLoading && initError && (
              <div className="tenant-screening-payment-error">
                {initError}
              </div>
            )}
          </Elements>
        ) : (
          <div className="tenant-screening-payment-form">
            <div className="tenant-screening-payment-header">
              <h1 className="tenant-screening-payment-title">Complete Payment</h1>
              <p className="tenant-screening-payment-subtitle">Secure payment for tenant screening</p>
            </div>
            <div className="tenant-screening-payment-error">
              Stripe is not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in .env
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantScreeningPayment;
